import React, { useState, useRef, useEffect } from 'react';
import { MapPinIcon, MagnifyingGlassIcon, GlobeAltIcon } from '@heroicons/react/24/outline';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { locationAPI } from '../../services/api';

// Fix Leaflet marker icon issue
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

interface PlacePrediction {
  place_id: string;
  display_name: string;
  lat: string;
  lon: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
}

interface LocationPickerProps {
  value: string;
  onChange: (location: string, coordinates?: { lat: number; lng: number }) => void;
  placeholder?: string;
  className?: string;
  initialCoordinates?: { lat?: number; lng?: number };
  saveCoordinates?: boolean;
  id?: string;
  name?: string;
}

// Component to update map center when coordinates change
const MapUpdater: React.FC<{ center: { lat: number; lng: number } }> = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    map.setView(center, map.getZoom());
  }, [center, map]);
  return null;
};

// Component to handle map clicks and marker dragging
const LocationMarker: React.FC<{
  position: { lat: number; lng: number };
  setPosition: (pos: { lat: number; lng: number }) => void;
  onLocationFound: (lat: number, lng: number) => void;
}> = ({ position, setPosition, onLocationFound }) => {
  const markerRef = useRef<L.Marker>(null);

  useMapEvents({
    click(e) {
      setPosition(e.latlng);
      onLocationFound(e.latlng.lat, e.latlng.lng);
    },
  });

  const eventHandlers = React.useMemo(
    () => ({
      dragend() {
        const marker = markerRef.current;
        if (marker) {
          const newPos = marker.getLatLng();
          setPosition(newPos);
          onLocationFound(newPos.lat, newPos.lng);
        }
      },
    }),
    [onLocationFound, setPosition]
  );

  return (
    <Marker
      draggable={true}
      eventHandlers={eventHandlers}
      position={position}
      ref={markerRef}
    />
  );
};

const LocationPicker: React.FC<LocationPickerProps> = ({ 
  value, 
  onChange, 
  placeholder = "Enter location...",
  className = "",
  saveCoordinates = false,
  initialCoordinates,
  id,
  name
}) => {
  const [suggestions, setSuggestions] = useState<PlacePrediction[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentCenter, setCurrentCenter] = useState<{ lat: number; lng: number }>(
    initialCoordinates && initialCoordinates.lat != null && initialCoordinates.lng != null
      ? { lat: initialCoordinates.lat, lng: initialCoordinates.lng }
      : { lat: 20.5937, lng: 78.9629 } // Default to India center
  );
  
  const inputRef = useRef<HTMLInputElement>(null);

  const { lat: initialLat, lng: initialLng } = initialCoordinates || {};
  const userLocationRef = useRef<{lat: number, lng: number} | null>(null);

  useEffect(() => {
    if (initialLat != null && initialLng != null) {
      setCurrentCenter({ lat: initialLat, lng: initialLng });
    }
  }, [initialLat, initialLng]);

  // Attempt to quietly fetch user's actual location to prioritize search results
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          userLocationRef.current = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
        },
        () => {
          // ignore errors for passive location fetching
        }
      );
    }
  }, []);

  const searchLocations = async (query: string) => {
    if (!query || query.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setIsLoading(true);
    try {
      // Use Photon API for better autocomplete/partial matching
      // Prioritize search results by adding the lat & lon based on user's current location or map center
      const biasLat = userLocationRef.current?.lat || currentCenter.lat;
      const biasLng = userLocationRef.current?.lng || currentCenter.lng;
      
      let url = `https://photon.komoot.io/api/?q=${encodeURIComponent(query)}&limit=50`;
      if (biasLat && biasLng) {
        url += `&lat=${biasLat}&lon=${biasLng}`;
      }

      const response = await fetch(url);
      const data = await response.json();
      
      // Filter for India (countrycode 'IN')
      const filteredFeatures = (data.features || []).filter(
        (feature: any) => feature.properties?.countrycode === 'IN'
      );

      // Prioritize places (cities, towns, districts, states) over roads/streets/venues
      const getPriorityScore = (feature: any) => {
        const props = feature.properties || {};
        const key = props.osm_key;
        const val = props.osm_value;
        if (key === 'place' && ['city', 'town', 'village', 'district', 'state', 'region', 'municipality'].includes(val)) {
          return 3;
        }
        if (key === 'place') {
          return 2;
        }
        if (key === 'boundary' || val === 'administrative') {
          return 1;
        }
        return 0;
      };

      const sortedFeatures = [...filteredFeatures].sort((a, b) => {
        const scoreA = getPriorityScore(a);
        const scoreB = getPriorityScore(b);
        return scoreB - scoreA;
      });

      // Map to PlacePrediction format
      const mappedSuggestions = sortedFeatures.map((feature: any) => {
          const props = feature.properties;
          
          // Determine Main Text (Name of the place, street, or city)
          const mainText = props.name || props.street || props.city || props.district || props.state;
          
          // Determine Secondary Text (Context: City, State, Country)
          const secondaryParts = [
            props.street,
            props.suburb,
            props.district,
            props.city,
            props.state,
            props.country
          ].filter(part => part && part !== mainText); // Exclude if same as main
          
          // Remove duplicates from secondary parts
          const uniqueSecondary = Array.from(new Set(secondaryParts));
          const secondaryText = uniqueSecondary.join(', ');

          // Construct full display name for the input field
          const displayName = mainText 
            ? `${mainText}, ${secondaryText}`
            : secondaryText;

          return {
            place_id: String(props.osm_id),
            display_name: displayName,
            lat: String(feature.geometry.coordinates[1]),
            lon: String(feature.geometry.coordinates[0]),
            structured_formatting: {
              main_text: mainText,
              secondary_text: secondaryText
            }
          };
        });

      // Deduplicate based on display_name and limit to top 10 for display
      const uniqueSuggestions = mappedSuggestions
        .filter((item: PlacePrediction, index: number, self: PlacePrediction[]) =>
          index === self.findIndex((t) => t.display_name === item.display_name)
        )
        .slice(0, 10);

      setSuggestions(uniqueSuggestions);
      setShowSuggestions(true);
    } catch (error) {
      console.error('Error fetching location suggestions:', error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    searchLocations(newValue);
  };

  const selectLocation = (prediction: PlacePrediction) => {
    const locationName = prediction.display_name;
    const lat = parseFloat(prediction.lat);
    const lng = parseFloat(prediction.lon);
    
    onChange(locationName, { lat, lng });
    setCurrentCenter({ lat, lng });
    setSuggestions([]);
    setShowSuggestions(false);
    
    if (saveCoordinates) {
      saveUserLocation(locationName, lat, lng);
    }
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by this browser.');
      return;
    }

    setIsLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setCurrentCenter({ lat: latitude, lng: longitude });
        
        try {
          // Reverse geocode
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
          );
          const data = await response.json();
          const locationName = data.display_name;
          
          onChange(locationName, { lat: latitude, lng: longitude });
          if (saveCoordinates) {
            saveUserLocation(locationName, latitude, longitude);
          }
        } catch (error) {
          console.error('Error reverse geocoding:', error);
          onChange(`${latitude}, ${longitude}`, { lat: latitude, lng: longitude });
        } finally {
          setIsLoading(false);
        }
      },
      (error) => {
        setIsLoading(false);
        console.error('Error getting location:', error);
        alert('Unable to retrieve your location. Please enter manually.');
      }
    );
  };

  const handleMapLocationFound = React.useCallback(async (lat: number, lng: number) => {
    // console.log('handleMapLocationFound called with:', lat, lng);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
      );
      const data = await response.json();
      const locationName = data.display_name;
      // console.log('Reverse geocoding result:', locationName);
      
      onChange(locationName, { lat, lng });
      if (saveCoordinates) {
        saveUserLocation(locationName, lat, lng);
      }
    } catch (error) {
      console.error('Error reverse geocoding:', error);
    }
  }, [onChange, saveCoordinates]);

  const saveUserLocation = async (location: string, lat: number, lng: number) => {
    try {
      await locationAPI.updateUserLocation({
        latitude: lat,
        longitude: lng,
        location: location,
      });
    } catch (error) {
      console.error('Failed to save location coordinates:', error);
    }
  };

  return (
    <div className="relative">
      {/* Input Field */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
        </div>
        <input
          ref={inputRef}
          id={id}
          name={name}
          type="text"
          value={value}
          onChange={handleInputChange}
          onFocus={() => value && searchLocations(value)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          className={`input-field pl-9 pr-14 !bg-transparent !border-none !ring-0 !outline-none ${className}`}
          placeholder={placeholder}
        />
        
        <div className="absolute inset-y-0 right-0 flex items-center space-x-1 pr-3">
          {/* Current Location Button */}
          <button
            type="button"
            onClick={getCurrentLocation}
            disabled={isLoading}
            className="p-1 text-gray-400 hover:text-primary-600 transition-colors disabled:opacity-50"
            title="Use current location"
          >
            {isLoading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500"></div>
            ) : (
              <MapPinIcon className="h-4 w-4" />
            )}
          </button>
          
          {/* Map Toggle Button */}
          <button
            type="button"
            onClick={() => setShowMap(!showMap)}
            className={`p-1 transition-colors ${
              showMap ? 'text-primary-600' : 'text-gray-400 hover:text-primary-600'
            }`}
            title="Select on map"
          >
            <GlobeAltIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Suggestions Dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-auto">
          {suggestions.map((prediction) => (
            <button
              key={prediction.place_id}
              type="button"
              onClick={() => selectLocation(prediction)}
              className="w-full px-4 py-3 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none transition-colors border-b border-gray-50 last:border-0"
            >
              <div className="flex items-center">
                <MapPinIcon className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900 text-sm truncate">
                    {prediction.structured_formatting.main_text}
                  </div>
                  {prediction.structured_formatting.secondary_text && (
                    <div className="text-xs text-gray-500 truncate">
                      {prediction.structured_formatting.secondary_text}
                    </div>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Map Container */}
      {showMap && (
        <div className="absolute z-30 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
          <div className="p-3 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-gray-700">Select location on map</p>
              <button
                onClick={() => setShowMap(false)}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Close
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Click on the map or drag the marker to select a location
            </p>
          </div>
          <div className="w-full h-64 relative z-0">
            <MapContainer
              center={currentCenter}
              zoom={13}
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <MapUpdater center={currentCenter} />
              <LocationMarker 
                position={currentCenter} 
                setPosition={setCurrentCenter}
                onLocationFound={handleMapLocationFound}
              />
            </MapContainer>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationPicker;
